import { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiClock, FiLoader, FiShield } from "react-icons/fi";
import { getMintSpaceKyb, submitMintSpaceKyb } from "../../api/mintSpace";
import { emptyVerification } from "../../constants/kyc";
import {
  clearErrorsAt,
  serverFieldErrors,
  serverMessage,
  setIn,
  validateVerification,
  verificationPayload,
  verificationToForm,
} from "../../utils/kyc";
import ApplicantTypePicker from "./ApplicantTypePicker";
import KybForm from "./KybForm";
import KycForm from "./KycForm";
import { SectionTitle } from "./KycFields";

const STATE = {
  none: { icon: FiShield, tone: "border-accent-default bg-accent-tinted text-accent-text" },
  pending: { icon: FiClock, tone: "border-status-warning bg-status-warning-bg text-status-warning" },
  approved: { icon: FiCheckCircle, tone: "border-status-success bg-status-success-bg text-status-success" },
  declined: { icon: FiAlertTriangle, tone: "border-status-danger bg-status-danger-bg text-status-danger" },
};

/**
 * A business's verification (KYB), for its owner: where it stands, and the form — an
 * individual's or a company's, whichever they say the business is registered to. A
 * business created before verification existed fills it in here; one an admin declined
 * is corrected and sent again, as the other kind if that was the mistake. Once
 * approved, it's read-only.
 *
 * @param {{ isDark: boolean, t: Function, mintSpaceId: string, setToast: Function, onStatusChange?: (status: string) => void }} props
 */
export default function BusinessVerificationPanel({ isDark, t, mintSpaceId, setToast, onStatusChange }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("none");
  const [application, setApplication] = useState(null);
  const [form, setForm] = useState(emptyVerification);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  /** What the other kind's answers start from, should the owner switch. */
  const prefill = useRef(null);
  // Held in a ref so a parent passing a fresh callback each render doesn't refetch.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const card = "border border-separator-default bg-bg-raised";

  const apply = useCallback((data) => {
    const next = data?.status || "none";
    if (data?.prefill) prefill.current = data.prefill;
    setStatus(next);
    setApplication(data?.application || null);
    setForm(verificationToForm({ ...prefill.current, ...data?.application }));
    setErrors({});
    onStatusChangeRef.current?.(next);
  }, []);

  useEffect(() => {
    if (!mintSpaceId) return;
    let alive = true;
    setLoading(true);
    getMintSpaceKyb(mintSpaceId)
      .then((res) => alive && apply(res.data))
      .catch((err) => alive && setToast?.(serverMessage(err) || t("kyc.errors.generic")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [mintSpaceId, apply, setToast, t]);

  const locked = status === "approved";

  const change = (path, value) => {
    setForm((prev) => setIn(prev, path, value));
    setErrors((prev) => clearErrorsAt(prev, path));
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateVerification(form, t);
    setErrors(found);
    if (Object.keys(found).length) {
      setToast?.(t("kyc.errors.fixBelow"));
      return;
    }
    setSaving(true);
    try {
      const res = await submitMintSpaceKyb(mintSpaceId, verificationPayload(form));
      apply(res.data);
      setToast?.(t("kyc.business.submitted"));
    } catch (err) {
      const fieldErrors = serverFieldErrors(err);
      if (fieldErrors) {
        setErrors(fieldErrors);
        setToast?.(t("kyc.errors.fixBelow"));
      } else {
        setToast?.(serverMessage(err) || t("kyc.errors.generic"));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <FiLoader className="h-6 w-6 animate-spin text-accent-text" />
      </div>
    );
  }

  const { icon: Icon, tone } = STATE[status] || STATE.none;
  const submittedOn = application?.submittedAt
    ? new Date(application.submittedAt).toLocaleDateString()
    : "";
  const formProps = { form, onChange: change, errors, disabled: locked, isDark, t };

  return (
    <div>
      <div className={`mb-6 flex items-start gap-3 rounded-radius-md border p-4 ${tone}`}>
        <Icon className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="min-w-0">
          <p className="text-subheadline-semibold">{t(`kyc.business.${status}Title`)}</p>
          <p className={`mt-0.5 text-subheadline ${mutedText}`}>{t(`kyc.business.${status}Body`)}</p>
          {status === "declined" && application?.decisionReason && (
            <p className="mt-2 text-subheadline-semibold">
              {t("kyc.business.reason", { reason: application.decisionReason })}
            </p>
          )}
          {submittedOn && (
            <p className={`mt-2 text-footnote ${mutedText}`}>
              {t("kyc.business.submittedAt", { date: submittedOn })}
            </p>
          )}
        </div>
      </div>

      <form onSubmit={submit} noValidate className={`rounded-radius-md p-4 sm:p-6 ${card} ${baseText}`}>
        <section className={form.applicantType ? "mb-8" : ""}>
          <SectionTitle title={t("kyc.applicant.title")} isDark={isDark} />
          <ApplicantTypePicker
            value={form.applicantType}
            onChange={(v) => change("applicantType", v)}
            error={errors.applicantType}
            disabled={locked}
            isDark={isDark}
            t={t}
          />
        </section>

        {form.applicantType === "individual" && <KycForm {...formProps} />}
        {form.applicantType === "company" && <KybForm {...formProps} />}

        {!locked && form.applicantType && (
          <button
            type="submit"
            disabled={saving}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-radius-sm bg-accent-default px-5 py-3 text-subheadline-semibold text-accent-on shadow-lg transition disabled:opacity-50 sm:w-auto"
          >
            {saving && <FiLoader className="h-4 w-4 animate-spin" />}
            {status === "pending"
              ? t("kyc.business.update")
              : status === "declined"
              ? t("kyc.business.resubmit")
              : t("kyc.business.submit")}
          </button>
        )}
      </form>
    </div>
  );
}
